import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ConsumerLayout from './ConsumerLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { orders, getErrorMessage } from '../../utils/api';
import { toast } from 'sonner';
import { ShoppingBag, CreditCard, Clock, Zap, Calendar } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { HelpTooltip } from '../shared/HelpTooltip';
import { useLanguage } from '../../contexts/LanguageContext';
import StoreHoursBanner from './StoreHoursBanner';
import SavingsSummaryCard from './SavingsSummaryCard';

const stripePromise = loadStripe('pk_test_51QntqsH7xN6aWpSEvUQGMWMJhp3Ua9gzmFZgJr4vD1XUcKTFzBnAGEbVGKWz2nSPvRQcCKEh0pGfOPPP1OxgJxvI00Edc9E3wv');

// Helper to generate available time slots
const generateTimeSlots = (earliestTime, closingTime) => {
  const slots = [];
  const earliest = new Date(earliestTime);
  const closing = new Date(closingTime);
  
  // Round up earliest to next 15-minute slot
  const minutes = earliest.getMinutes();
  const roundedMinutes = Math.ceil(minutes / 15) * 15;
  earliest.setMinutes(roundedMinutes, 0, 0);
  
  // Generate slots from earliest to closing - 1 hour
  const lastSlot = new Date(closing.getTime() - 60 * 60 * 1000); // 1 hour before closing
  
  let current = new Date(earliest);
  while (current <= lastSlot) {
    slots.push(new Date(current));
    current = new Date(current.getTime() + 15 * 60 * 1000); // 15-minute increments
  }
  
  return slots;
};

function CheckoutForm({ user, onLogout, cart, setCart }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [deliveryMethod, setDeliveryMethod] = useState('pickup');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [taxQuote, setTaxQuote] = useState(null);
  const [deliveryType, setDeliveryType] = useState('asap'); // 'asap' or 'scheduled'
  const [scheduledTime, setScheduledTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [deliveryWindow, setDeliveryWindow] = useState(null);
  const [pickupTime, setPickupTime] = useState('');
  const [charityRoundup, setCharityRoundup] = useState(false);
  const [loading, setLoading] = useState(false);
  // Pilot soft-lock: assume pickup-only until backend confirms otherwise.
  const [deliveryAvailable, setDeliveryAvailable] = useState(false);

  // Get retailer ID from cart (assuming all items from same retailer)
  const retailerId = cart.length > 0 ? cart[0].drlp_id : null;
  const [retailerDoc, setRetailerDoc] = useState(null);

  // Fetch retailer profile once so StoreHoursBanner can compute closing-soon
  useEffect(() => {
    if (!retailerId) return;
    let cancelled = false;
    if (orders.getRetailerProfile) {
      orders.getRetailerProfile(retailerId)
        .then(r => { if (!cancelled) setRetailerDoc(r?.data || null); })
        .catch(err => {
          // Surface the failure so future route regressions don't hide silently.
          if (!cancelled) {
            console.warn('getRetailerProfile failed', err?.response?.status, err?.message);
          }
        });
    }
    return () => { cancelled = true; };
  }, [retailerId]);

  // Pilot soft-lock: ask the backend whether this retailer accepts delivery.
  // Default is pickup-only; admin must explicitly opt a retailer in.
  useEffect(() => {
    if (!retailerId) return;
    let cancelled = false;
    orders.getRetailerFulfillment(retailerId)
      .then((res) => {
        if (cancelled) return;
        const allowed = !!res?.data?.delivery_enabled;
        setDeliveryAvailable(allowed);
        if (!allowed) setDeliveryMethod('pickup');
      })
      .catch(() => {
        if (cancelled) return;
        setDeliveryAvailable(false);
        setDeliveryMethod('pickup');
      });
    return () => { cancelled = true; };
  }, [retailerId]);

  // Fetch delivery window when delivery is selected
  useEffect(() => {
    if (deliveryMethod === 'delivery' && retailerId) {
      fetchDeliveryWindow();
    }
  }, [deliveryMethod, retailerId]);

  const fetchDeliveryWindow = async () => {
    try {
      const response = await orders.getDeliveryWindow(retailerId);
      const windowData = response.data;
      setDeliveryWindow(windowData);
      
      if (windowData.is_open) {
        const slots = generateTimeSlots(windowData.earliest_delivery, windowData.latest_delivery);
        setAvailableSlots(slots);
        // Set default scheduled time to earliest if available
        if (slots.length > 0) {
          setScheduledTime(slots[0].toISOString());
        }
      }
    } catch (error) {
      console.error('Failed to fetch delivery window:', error);
      // Fallback: generate default slots (1 hour from now to 9 PM)
      const now = new Date();
      const earliest = new Date(now.getTime() + 60 * 60 * 1000);
      const closing = new Date();
      closing.setHours(21, 0, 0, 0); // 9 PM default
      
      const slots = generateTimeSlots(earliest, closing);
      setAvailableSlots(slots);
      if (slots.length > 0) {
        setScheduledTime(slots[0].toISOString());
      }
    }
  };

  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const tax = taxQuote ? taxQuote.order_tax_total : 0;
  const deliveryFee = deliveryMethod === 'delivery' ? 5.99 : 0;
  const netProceed = subtotal + tax + deliveryFee;
  const charityDac = netProceed * 0.0045;
  const charityDrlp = netProceed * 0.0045;
  const charityRoundupAmount = charityRoundup ? Math.ceil(netProceed + charityDac + charityDrlp) - (netProceed + charityDac + charityDrlp) : 0;
  const total = netProceed + charityDac + charityDrlp + charityRoundupAmount;

  // Fetch authoritative VA tax quote (per-line, NoVA locality) whenever the
  // cart or delivery method changes — keeps the preview in lock-step with the
  // amount actually charged at checkout.
  useEffect(() => {
    let cancelled = false;
    if (!cart || cart.length === 0) {
      setTaxQuote(null);
      return;
    }
    orders.taxQuote({
      items: cart.map((i) => ({ rshd_id: i.rshd_id, price: i.price, quantity: i.quantity })),
      delivery_method: deliveryMethod,
    })
      .then((res) => { if (!cancelled) setTaxQuote(res.data); })
      .catch(() => { if (!cancelled) setTaxQuote(null); });
    return () => { cancelled = true; };
  }, [cart, deliveryMethod]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    if (deliveryMethod === 'delivery' && !deliveryAddress) {
      toast.error('Please enter delivery address');
      return;
    }

    if (deliveryMethod === 'delivery' && deliveryType === 'scheduled' && !scheduledTime) {
      toast.error('Please select a delivery time');
      return;
    }

    if (deliveryMethod === 'pickup' && !pickupTime) {
      toast.error('Please select pickup time');
      return;
    }

    setLoading(true);

    try {
      // Create payment method
      const cardElement = elements.getElement(CardElement);
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        toast.error(error.message);
        setLoading(false);
        return;
      }

      // Create order
      const orderData = {
        items: cart,
        delivery_method: deliveryMethod,
        delivery_address: deliveryMethod === 'delivery' ? deliveryAddress : null,
        delivery_type: deliveryMethod === 'delivery' ? deliveryType : null,
        scheduled_delivery_time: deliveryMethod === 'delivery' && deliveryType === 'scheduled' ? scheduledTime : null,
        pickup_time: deliveryMethod === 'pickup' ? pickupTime : null,
        charity_roundup: charityRoundupAmount,
        payment_method_id: paymentMethod.id,
        dac_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Capture DAC's timezone
      };

      await orders.create(orderData);
      toast.success('Order placed successfully!');
      localStorage.removeItem('cart');
      setCart([]);
      navigate('/consumer/orders');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to place order'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ConsumerLayout user={user} onLogout={onLogout}>
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Checkout
          </h1>
          <p className="text-gray-600 mt-1">Complete your order</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contextual banners */}
            <StoreHoursBanner retailer={retailerDoc} />
            <SavingsSummaryCard cart={cart} roundUp={charityRoundupAmount} />

            {/* Delivery Method */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  Delivery Method
                  <HelpTooltip portal="consumer" category="checkout" tooltipKey="delivery" t={t} size="sm" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup value={deliveryMethod} onValueChange={setDeliveryMethod}>
                  <div className="flex items-center space-x-2 p-3 border rounded-lg">
                    <RadioGroupItem value="pickup" id="pickup" data-testid="pickup-option" />
                    <Label htmlFor="pickup" className="flex-1 cursor-pointer">
                      <div>
                        <p className="font-medium">Pickup</p>
                        <p className="text-sm text-gray-500">Free</p>
                      </div>
                    </Label>
                  </div>
                  {deliveryAvailable && (
                    <div className="flex items-center space-x-2 p-3 border rounded-lg mt-2">
                      <RadioGroupItem value="delivery" id="delivery" data-testid="delivery-option" />
                      <Label htmlFor="delivery" className="flex-1 cursor-pointer">
                        <div>
                          <p className="font-medium">Delivery (DoorDash)</p>
                          <p className="text-sm text-gray-500">$5.99</p>
                        </div>
                      </Label>
                    </div>
                  )}
                </RadioGroup>

                {!deliveryAvailable && (
                  <p
                    className="mt-3 text-xs text-gray-500"
                    data-testid="pickup-only-notice"
                  >
                    Pickup only for this retailer right now. Delivery is rolling out soon.
                  </p>
                )}

                {deliveryMethod === 'delivery' && (
                  <div className="mt-4 space-y-4">
                    <div>
                      <Label htmlFor="address">Delivery Address</Label>
                      <Input
                        id="address"
                        data-testid="delivery-address"
                        placeholder="123 Main St, City, State ZIP"
                        value={deliveryAddress}
                        onChange={(e) => setDeliveryAddress(e.target.value)}
                        required
                      />
                    </div>

                    {/* Delivery Scheduling Options */}
                    <div className="border-t pt-4">
                      <Label className="text-base font-semibold mb-3 block">When do you want your delivery?</Label>
                      <RadioGroup value={deliveryType} onValueChange={setDeliveryType} className="space-y-2">
                        {/* ASAP Option */}
                        <div className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${deliveryType === 'asap' ? 'border-purple-500 bg-purple-50' : 'hover:bg-gray-50'}`}>
                          <RadioGroupItem value="asap" id="asap" data-testid="delivery-asap" />
                          <Label htmlFor="asap" className="flex-1 cursor-pointer">
                            <div className="flex items-center gap-2">
                              <Zap className="w-5 h-5 text-orange-500" />
                              <div>
                                <p className="font-medium">ASAP</p>
                                <p className="text-sm text-gray-500">40-60 minutes after order is ready</p>
                              </div>
                            </div>
                          </Label>
                        </div>

                        {/* Scheduled Option */}
                        <div className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${deliveryType === 'scheduled' ? 'border-purple-500 bg-purple-50' : 'hover:bg-gray-50'}`}>
                          <RadioGroupItem value="scheduled" id="scheduled" data-testid="delivery-scheduled" />
                          <Label htmlFor="scheduled" className="flex-1 cursor-pointer">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-5 h-5 text-purple-600" />
                              <div>
                                <p className="font-medium">Schedule for later</p>
                                <p className="text-sm text-gray-500">Choose your preferred delivery time</p>
                              </div>
                            </div>
                          </Label>
                        </div>
                      </RadioGroup>

                      {/* Time Slot Selector - Only show when scheduled is selected */}
                      {deliveryType === 'scheduled' && (
                        <div className="mt-4 pl-8">
                          <Label htmlFor="scheduled-time" className="flex items-center gap-2 mb-2">
                            <Clock className="w-4 h-4 text-gray-500" />
                            Select Delivery Time
                          </Label>
                          {availableSlots.length > 0 ? (
                            <Select value={scheduledTime} onValueChange={setScheduledTime}>
                              <SelectTrigger data-testid="delivery-time-select" className="w-full">
                                <SelectValue placeholder="Select a time slot" />
                              </SelectTrigger>
                              <SelectContent>
                                {availableSlots.map((slot, index) => (
                                  <SelectItem key={index} value={slot.toISOString()}>
                                    {slot.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    {index === 0 && ' (Earliest)'}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                              No delivery slots available for today. The store may be closed or it's too late to schedule.
                            </p>
                          )}
                          {deliveryWindow && (
                            <p className="text-xs text-gray-500 mt-2">
                              Earliest: {new Date(deliveryWindow.earliest_delivery).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} • 
                              Latest: {new Date(deliveryWindow.latest_delivery).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {deliveryMethod === 'pickup' && (
                  <div className="mt-4">
                    <Label htmlFor="pickup-time">Pickup Time</Label>
                    <Input
                      id="pickup-time"
                      data-testid="pickup-time"
                      type="datetime-local"
                      value={pickupTime}
                      onChange={(e) => setPickupTime(e.target.value)}
                      required
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Payment */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  Payment Information
                  <HelpTooltip portal="consumer" category="checkout" tooltipKey="payment" t={t} size="sm" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg p-3 bg-gray-50">
                  <CardElement
                    options={{
                      style: {
                        base: {
                          fontSize: '16px',
                          color: '#424770',
                          '::placeholder': { color: '#aab7c4' },
                        },
                      },
                    }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Test card: 4242 4242 4242 4242, any future date, any CVC
                </p>
              </CardContent>
            </Card>

            {/* Charity Roundup */}
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="roundup"
                    data-testid="charity-roundup"
                    checked={charityRoundup}
                    onCheckedChange={setCharityRoundup}
                  />
                  <Label htmlFor="roundup" className="cursor-pointer flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900">Round up for charity</p>
                      <HelpTooltip portal="consumer" category="checkout" tooltipKey="roundup" t={t} size="sm" />
                    </div>
                    <p className="text-sm text-gray-600">
                      Round up your total to ${Math.ceil(total - charityRoundupAmount).toFixed(2)} and donate{' '}
                      ${charityRoundupAmount.toFixed(2)} to your chosen charity
                    </p>
                  </Label>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Items */}
                <div className="space-y-2">
                  {cart.map((item, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span className="text-gray-600">
                        {item.name} x{item.quantity}
                      </span>
                      <span className="font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t pt-3 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal</span>
                    <span data-testid="subtotal">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Sales Tax (VA)</span>
                    <span data-testid="tax">${tax.toFixed(2)}</span>
                  </div>
                  {deliveryFee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Delivery Fee</span>
                      <span data-testid="delivery-fee">${deliveryFee.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-emerald-600">
                    <span>Charity (DAC)</span>
                    <span data-testid="charity-dac">${charityDac.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-emerald-600">
                    <span>Charity (Retailer)</span>
                    <span data-testid="charity-drlp">${charityDrlp.toFixed(2)}</span>
                  </div>
                  {charityRoundup && (
                    <div className="flex justify-between text-emerald-600">
                      <span>Charity Round-up</span>
                      <span data-testid="charity-roundup-amount">${charityRoundupAmount.toFixed(2)}</span>
                    </div>
                  )}
                </div>

                <div className="border-t pt-3">
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span data-testid="total">${total.toFixed(2)}</span>
                  </div>
                </div>

                <Button
                  data-testid="place-order-btn"
                  onClick={handleSubmit}
                  disabled={!stripe || loading}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  {loading ? 'Processing...' : 'Place Order'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </ConsumerLayout>
  );
}

export default function ConsumerCheckout({ user, onLogout }) {
  const [cart, setCart] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    } else {
      navigate('/consumer/browse');
    }
  }, [navigate]);

  if (cart.length === 0) {
    return (
      <ConsumerLayout user={user} onLogout={onLogout}>
        <Card>
          <CardContent className="py-12 text-center">
            <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">Your cart is empty</p>
            <Button onClick={() => navigate('/consumer/browse')} className="mt-4">
              Browse Deals
            </Button>
          </CardContent>
        </Card>
      </ConsumerLayout>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm user={user} onLogout={onLogout} cart={cart} setCart={setCart} />
    </Elements>
  );
}
